import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import branding from '../branding.json';

// ── Existing components (direct reuse, zero changes) ──
import StatCard from '../components/common/StatCard';
import DashboardTicker from '../components/dashboards/DashboardTicker';
import NeedsAttention from '../components/dashboards/NeedsAttention';
import RecentWins from '../components/dashboards/RecentWins';
import ErrorBoundary from '../components/common/ErrorBoundary';

// ── New home components ──
import { StationVitals } from '../components/health/StationVitals';
import ResumeCards from '../components/home/ResumeCards';
import GymnasiumSpotlight from '../components/home/GymnasiumSpotlight';
import QuickActions from '../components/home/QuickActions';
import PhaseHorizon from '../components/home/PhaseHorizon';
import GoalProgressWidget from '../components/home/GoalProgressWidget';
import CalendarPreviewWidget from '../components/home/CalendarPreviewWidget';
import CommandCenterZone from '../components/home/CommandCenterZone';

// ── New common components ──
import TemporalPulse from '../components/common/TemporalPulse';

// ── Hooks ──
import { useBootSequence } from '../hooks/useBootSequence';

// ── Services ──
import { analyticsService } from '../services/analyticsService';
import { gymnasiumService } from '../services/gymnasiumService';
import { workService } from '../services/workService';
import api from '../services/api';
import { scheduleService } from '../services/scheduleService';

// ── Types ──
import { EloRating, Match } from '../types/gymnasium';
import type { Task, Goal } from '../types/work';

// ── Helpers ──

/** Compute 14-day event count trend for a given category */
function computeTrend(events: any[], category: string): number[] {
  const counts: Record<string, number> = {};
  const now = new Date();
  // Initialize 14 days with zeros
  for (let i = 13; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    counts[d.toISOString().slice(0, 10)] = 0;
  }
  // Count events per day
  for (const evt of events) {
    if (evt.category === category) {
      const day = new Date(evt.created_at).toISOString().slice(0, 10);
      if (day in counts) counts[day]++;
    }
  }
  return Object.values(counts);
}

/** Derive trend direction from last 6 days split in half */
function trendDir(arr: number[]): 'up' | 'down' | 'flat' {
  if (arr.length < 6) return 'flat';
  const recent = arr.slice(-3).reduce((s, v) => s + v, 0);
  const earlier = arr.slice(-6, -3).reduce((s, v) => s + v, 0);
  return recent > earlier ? 'up' : recent < earlier ? 'down' : 'flat';
}

function timeGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

/** Inline fallback for ErrorBoundary */
function ZoneFallback({ label }: { label: string }) {
  return (
    <div className="text-center py-6 text-sm text-gray-500">
      Phase {label} failed to initialize — <button onClick={() => window.location.reload()} className="underline">retry</button>
    </div>
  );
}

/** Rotating tagline hook — cycles through branding taglines */
function useRotatingTagline(interval = 4000): string {
  const taglines: string[] = (branding as any).theme?.animation?.taglines || [];
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (taglines.length <= 1) return;
    const timer = setInterval(() => setIndex(i => (i + 1) % taglines.length), interval);
    return () => clearInterval(timer);
  }, [taglines.length, interval]);
  return taglines[index] || '';
}

/** Stagger-in animation variants for zones */
const zoneVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, duration: 0.5, ease: 'easeOut' },
  }),
};

// ── Component ──

export const Home: React.FC = () => {
  const { darkMode } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const tagline = useRotatingTagline();

  const [events, setEvents] = useState<any[]>([]);
  const [personaSummary, setPersonaSummary] = useState<any>(null);
  const [workflowSummary, setWorkflowSummary] = useState<any>(null);
  const [artifactorySummary, setArtifactorySummary] = useState<any>(null);
  const [gauntletCount, setGauntletCount] = useState(0);
  const [matchCount, setMatchCount] = useState(0);
  const [ratings, setRatings] = useState<EloRating[]>([]);
  const [lastMatch, setLastMatch] = useState<Match | null>(null);
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [attentionItems, setAttentionItems] = useState<any[]>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<any[]>([]);

  // Boot sequence — cinematic init on first session load
  const { phase, isComplete, overlayText } = useBootSequence(!loading);

  useEffect(() => {
    document.title = branding.appName;
  }, []);

  const [lastLoadedAt, setLastLoadedAt] = useState<Date | null>(null);

  // Parallel data fetch — per-call .catch() guards for graceful partial failure
  const load = useCallback(async () => {
    try {
      const [evts, ps, ws, artSum, gauntlets, rankData, matches,
             tasksData, goalsData, attData, upcoming] = await Promise.all([
        analyticsService.getEvents({ limit: 500 }).catch(() => ({ events: [] })),
        analyticsService.getPersonaSummary().catch(() => null),
        analyticsService.getWorkflowSummary().catch(() => null),
        analyticsService.getArtifactorySummary().catch(() => null),
        gymnasiumService.listGauntlets().catch(() => []),
        gymnasiumService.getRankings().catch(() => ({ ratings: [], match_count: 0 })),
        gymnasiumService.listMatches().catch(() => []),
        workService.getTasks({ limit: 100 }).catch(() => []),
        workService.getGoals({ include_completed: false }).catch(() => []),
        api.get('/analytics/cross-system/attention-items').then(r => r.data?.items || []).catch(() => []),
        scheduleService.upcoming(168).catch(() => []),
      ]);
      // Normalize event field names: API returns event_category/event_metadata,
      // but computeTrend and TemporalPulse read category/metadata
      const rawEvts = evts.events || evts || [];
      const normalizedEvts = rawEvts.map((e: any) => ({
        ...e,
        category: e.event_category || e.category,
        metadata: e.event_metadata || e.metadata,
      }));
      setEvents(normalizedEvts);
      setPersonaSummary(ps);
      setWorkflowSummary(ws);
      setArtifactorySummary(artSum);
      setGauntletCount(Array.isArray(gauntlets) ? gauntlets.length : 0);
      setMatchCount(rankData.match_count || 0);
      setRatings(rankData.ratings || []);
      if (Array.isArray(matches) && matches.length > 0) setLastMatch(matches[0]);
      setTasks(Array.isArray(tasksData) ? tasksData : []);
      setGoals(Array.isArray(goalsData) ? goalsData : []);
      setAttentionItems(Array.isArray(attData) ? attData : []);
      setUpcomingEvents(Array.isArray(upcoming) ? upcoming : []);
    } catch (err) {
      console.error('Home load error:', err);
    } finally {
      setLoading(false);
      setLastLoadedAt(new Date());
    }
  }, []);

  // Fetch on mount
  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 5 minutes when page is visible
  useEffect(() => {
    const checkRefresh = () => {
      if (document.visibilityState !== 'visible' || !lastLoadedAt) return;
      if (Date.now() - lastLoadedAt.getTime() > 300000) {
        load();
      }
    };
    const interval = setInterval(checkRefresh, 60000);
    document.addEventListener('visibilitychange', checkRefresh);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', checkRefresh);
    };
  }, [lastLoadedAt, load]);

  // Compute trends from events
  const personaTrend = computeTrend(events, 'persona');
  const workflowTrend = computeTrend(events, 'workflow');
  const artifactoryTrend = computeTrend(events, 'artifactory');

  // Transform events for TemporalPulse
  const pulseEvents = events.map((e: any) => ({ timestamp: e.created_at, category: e.category }));

  // User display name
  const userName = user?.full_name || user?.username || user?.email?.split('@')[0] || 'Commander';

  // Memoized ticker events — stable reference prevents DashboardTicker useEffect re-fire on unrelated Home re-renders
  const tickerEvents = useMemo(() => events.slice(0, 8), [events]);

  // Board summary for greeting subtitle
  const now = new Date();
  const todayStr = now.toISOString().slice(0, 10);
  const tasksDueToday = tasks.filter(t =>
    t.due_date && t.due_date.slice(0, 10) === todayStr && t.status !== 'completed' && t.status !== 'cancelled'
  ).length;
  const overdueTasks = tasks.filter(t =>
    t.due_date && t.due_date.slice(0, 10) < todayStr && t.status !== 'completed' && t.status !== 'cancelled'
  ).length;
  const lastOpTime = events.length > 0 ? relativeTime(events[0]?.created_at) : null;

  return (
    <div className={`space-y-3 ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>

      {/* Boot sequence overlay */}
      {overlayText && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"
          aria-hidden="true"
        >
          <p className={`font-mono text-sm tracking-wider ${darkMode ? 'text-indigo-400/60' : 'text-indigo-600/40'}`}>
            {overlayText}
          </p>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════
          Zone 1: The Pulse
          ════════════════════════════════════════════════════════════════ */}
      <motion.div custom={0} variants={zoneVariants} initial="hidden" animate={phase >= 1 ? "visible" : "hidden"}>
      <ErrorBoundary fallback={<ZoneFallback label="The Pulse" />}>
      <div className={`rounded-xl px-4 py-3 relative overflow-hidden ${darkMode ? 'bg-gradient-to-b from-gray-900 to-gray-800/50' : 'bg-gradient-to-b from-gray-50 to-white'}`}>
        {/* Breathing glow */}
        <div
          className="absolute inset-0 pointer-events-none breathe-glow"
          style={{
            background: darkMode
              ? 'radial-gradient(ellipse at 30% 50%, rgba(99,102,241,0.15) 0%, transparent 60%)'
              : 'radial-gradient(ellipse at 30% 50%, rgba(99,102,241,0.08) 0%, transparent 60%)',
          }}
        />
        {/* breathe-glow class + keyframes now in global CSS (index.css) */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          {/* Left: Greeting + Stat Sparklines */}
          <div>
            <h1 className={`text-xl font-bold mb-0.5 ${darkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>
              {timeGreeting()}, {userName}
            </h1>
            {/* Animated tagline rotation */}
            <div className="h-5 mb-1 overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.p
                  key={tagline}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.4 }}
                  className={`text-xs italic ${darkMode ? 'text-indigo-400/60' : 'text-indigo-500/60'}`}
                >
                  {tagline}
                </motion.p>
              </AnimatePresence>
            </div>
            {(() => {
              const parts: string[] = [];
              if (tasksDueToday > 0) parts.push(`${tasksDueToday} task${tasksDueToday > 1 ? 's' : ''} due today`);
              if (overdueTasks > 0) parts.push(`${overdueTasks} overdue`);
              if (lastOpTime) parts.push(`Last operation: ${lastOpTime}`);
              const subtitle = loading
                ? 'Command center initializing...'
                : `Command center ready${parts.length > 0 ? '. ' + parts.join(' \u00b7 ') : '.'}`;
              return (
                <p className={`text-xs mb-2 font-mono ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {subtitle}
                </p>
              );
            })()}

            {/* Phase Status Horizon */}
            <PhaseHorizon activeNow={loading ? null : (workflowSummary?.active_now ?? 0)} />

            {/* NeedsAttention compact strip — only when items exist */}
            {!loading && attentionItems.length > 0 && (
              <ErrorBoundary fallback={<ZoneFallback label="Attention Monitor" />}>
                <NeedsAttention items={attentionItems} compact />
              </ErrorBoundary>
            )}

            {/* Station Vitals — always-visible health pill strip */}
            <StationVitals />

            <div className="flex gap-2 flex-wrap">
              {!loading ? (
                <>
                  <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0 }}>
                    <StatCard
                      label="Personas"
                      value={personaSummary?.total ?? '—'}
                      trend={personaTrend}
                      trendDirection={trendDir(personaTrend)}
                      color="#a855f7"
                      onClick={() => navigate('/dashboards/personas')}
                    />
                  </motion.div>
                  <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                    <StatCard
                      label="Phases"
                      value={workflowSummary?.total ?? '—'}
                      trend={workflowTrend}
                      trendDirection={trendDir(workflowTrend)}
                      color="#3b82f6"
                      onClick={() => navigate('/dashboards/workflows')}
                    />
                  </motion.div>
                  <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                    <StatCard
                      label="Artifacts"
                      value={artifactorySummary?.total ?? '—'}
                      trend={artifactoryTrend}
                      trendDirection={trendDir(artifactoryTrend)}
                      color="#06b6d4"
                      onClick={() => navigate('/dashboards/artifactory')}
                    />
                  </motion.div>
                  {workflowSummary?.active_now > 0 && (
                    <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                      <StatCard
                        label="Running Now"
                        value={workflowSummary.active_now}
                        color="#22c55e"
                        onClick={() => navigate('/dashboards/workflows')}
                      />
                    </motion.div>
                  )}
                </>
              ) : (
                [...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className={`p-4 rounded-xl border w-36 h-[72px] animate-pulse ${
                      darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-200'
                    }`}
                  />
                ))
              )}
            </div>
          </div>

          {/* Right: Live Ticker */}
          <div className="lg:max-w-md lg:min-w-[320px] flex-1">
            <DashboardTicker
              personaSummary={personaSummary ?? undefined}
              workflowSummary={workflowSummary ?? undefined}
              artifactorySummary={artifactorySummary ?? undefined}
              recentEvents={loading ? undefined : tickerEvents}
            />
          </div>
        </div>
      </div>
      </ErrorBoundary>
      </motion.div>

      {/* Gradient separator */}
      <motion.div
        className="h-px bg-gradient-to-r from-transparent via-indigo-500/20 to-transparent"
        initial={{ scaleX: isComplete ? 1 : 0 }}
        animate={isComplete ? { scaleX: 1 } : { scaleX: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        style={{ transformOrigin: 'center' }}
      />

      {/* ════════════════════════════════════════════════════════════════
          Zone 2: Your Work (3-column — Goals, Calendar, Wins+Resume)
          ════════════════════════════════════════════════════════════════ */}
      <motion.div custom={1} variants={zoneVariants} initial="hidden" animate={isComplete ? "visible" : "hidden"}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: Goal Progress */}
        <ErrorBoundary fallback={<ZoneFallback label="Goals" />}>
          {!loading ? (
            <GoalProgressWidget goals={goals} tasks={tasks} />
          ) : (
            <div className={`h-48 animate-pulse rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`} />
          )}
        </ErrorBoundary>

        {/* Center: Calendar Preview */}
        <ErrorBoundary fallback={<ZoneFallback label="Calendar" />}>
          {!loading ? (
            <CalendarPreviewWidget tasks={tasks} scheduledEvents={upcomingEvents} />
          ) : (
            <div className={`h-48 animate-pulse rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`} />
          )}
        </ErrorBoundary>

        {/* Right: Wins + Resume combined */}
        <div className={`rounded-lg border-l-4 border-violet-500 p-4 ${darkMode ? 'bg-gray-800/60' : 'bg-white'}`}>
          <ErrorBoundary fallback={<ZoneFallback label="Milestone Log" />}>
            <h3 className={`text-xs font-semibold mb-2 ${darkMode ? 'text-amber-400/80' : 'text-amber-600/80'}`}>Milestone Log</h3>
            <RecentWins />
          </ErrorBoundary>
          <div className={`h-px my-2 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
          <ErrorBoundary fallback={<ZoneFallback label="The Thread" />}>
            <h3 className={`text-xs font-semibold mb-2 ${darkMode ? 'text-indigo-400/80' : 'text-indigo-600/80'}`}>Pick Up The Thread</h3>
            <ResumeCards events={events} boardItems={{ tasks, projects: [] }} limit={3} />
          </ErrorBoundary>
        </div>
      </div>
      </motion.div>

      {/* Gradient separator */}
      <motion.div
        className="h-px bg-gradient-to-r from-transparent via-indigo-500/20 to-transparent"
        initial={{ scaleX: isComplete ? 1 : 0 }}
        animate={isComplete ? { scaleX: 1 } : { scaleX: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        style={{ transformOrigin: 'center' }}
      />

      {/* ════════════════════════════════════════════════════════════════
          Zone 2.5: Command Center Analytics
          ════════════════════════════════════════════════════════════════ */}
      <motion.div custom={2} variants={zoneVariants} initial="hidden" animate={isComplete ? "visible" : "hidden"}>
        <ErrorBoundary fallback={<ZoneFallback label="Command Center" />}>
          <CommandCenterZone upcomingEvents={upcomingEvents} />
        </ErrorBoundary>
      </motion.div>

      {/* Gradient separator */}
      <motion.div
        className="h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent"
        initial={{ scaleX: isComplete ? 1 : 0 }}
        animate={isComplete ? { scaleX: 1 } : { scaleX: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        style={{ transformOrigin: 'center' }}
      />

      {/* ════════════════════════════════════════════════════════════════
          Zone 3: Operations Grid (dynamic grid — reflows when gym hidden)
          ════════════════════════════════════════════════════════════════ */}
      <motion.div custom={3} variants={zoneVariants} initial="hidden" animate={isComplete ? "visible" : "hidden"}>
      <div className={`grid grid-cols-1 ${ratings.length > 0 ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-4`}>
        {/* Panel A: Arena Standing (conditionally rendered) */}
        {ratings.length > 0 && (
          <ErrorBoundary fallback={<ZoneFallback label="Arena Standing" />}>
            <GymnasiumSpotlight ratings={ratings} lastMatch={lastMatch} />
          </ErrorBoundary>
        )}

        {/* Panel B: Temporal Pulse */}
        <ErrorBoundary fallback={<ZoneFallback label="Temporal Pulse" />}>
          <div
            className={`rounded-lg shadow-lg border-l-4 p-3 ${
              darkMode ? 'bg-gray-800 border-indigo-600' : 'bg-white border-indigo-500 shadow-sm'
            }`}
          >
            <h2 className="text-sm font-semibold mb-1 bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-purple-400">
              Temporal Pulse
            </h2>
            {events.length > 0 ? (
              <TemporalPulse events={pulseEvents} days={30} accentColor="indigo" defaultCollapsed={true} />
            ) : (
              <p className={`text-xs text-center py-8 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Your activity will appear here
              </p>
            )}
          </div>
        </ErrorBoundary>

        {/* Panel C: Launch Pad */}
        <ErrorBoundary fallback={<ZoneFallback label="Launch Pad" />}>
          <div>
            <h2 className="text-sm font-semibold mb-1 bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-purple-400">
              Launch Pad
            </h2>
            <QuickActions />
          </div>
        </ErrorBoundary>
      </div>
      </motion.div>

      {/* Zone 4 removed — NeedsAttention promoted to Zone 1 compact strip */}
    </div>
  );
};
